import { type FC } from 'react';

interface ExpertiseCardProps {
  title: string;
  description: string;
  imageUrl: string;
}

const ExpertiseCard: FC<ExpertiseCardProps> = ({ title, description, imageUrl }) => (
  <div className="rounded-2xl overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.04)] bg-background-50">
    <div className="aspect-[4/5] relative overflow-hidden">
      <img
        src={imageUrl}
        alt=""
        className="w-full h-full object-cover"
      />
    </div>
    <div className="p-6">
      <p className="font-heading text-lg md:text-xl text-foreground-950 font-semibold leading-snug mb-2">
        {title}
      </p>
      <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
        {description}
      </p>
    </div>
  </div>
);

export default function ExpertiseSection() {
  return (
    <section className="relative w-full py-24 md:py-32 px-8 md:px-16 lg:px-24" style={{ backgroundColor: '#F5F2EB' }} aria-labelledby="expertise-title">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14 md:mb-20">
          <h2 id="expertise-title" className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground-950 leading-tight tracking-tight">
            Technical<br />Capabilities
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-md leading-relaxed">
            A focused set of engineering disciplines that define how we approach every project and deliver lasting value.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <ExpertiseCard
            title="System Architecture"
            description="Designing modular, scalable foundations that support long-term growth and seamless integration."
            imageUrl="https://readdy.ai/api/search-image?query=Abstract%20minimalist%20texture%20in%20muted%20sage%20green%20and%20warm%20stone%20tones%2C%20soft%20geometric%20gradient%20with%20delicate%20grain%2C%20no%20objects%20no%20people%2C%20fine%20art%20quality%2C%20atmospheric%20and%20airy%2C%20contemporary%20abstract%20composition&width=500&height=625&seq=expertise-01-bruny&orientation=portrait"
          />
          <ExpertiseCard
            title="Performance Optimization"
            description="Profiling and refining every layer of the stack to deliver fast, responsive, and efficient software."
            imageUrl="https://readdy.ai/api/search-image?query=Abstract%20soft%20layered%20texture%20in%20warm%20charcoal%20and%20pale%20beige%20tones%2C%20organic%20flowing%20gradients%20with%20subtle%20depth%2C%20no%20objects%20no%20people%2C%20minimalist%20fine%20art%2C%20delicate%20atmospheric%20composition%2C%20contemporary%20abstract%20aesthetic&width=500&height=625&seq=expertise-02-bruny&orientation=portrait"
          />
          <ExpertiseCard
            title="Quality Engineering"
            description="Rigorous testing, code review, and continuous improvement workflows that ensure reliability."
            imageUrl="https://readdy.ai/api/search-image?query=Abstract%20geometric%20texture%20in%20muted%20coral%20and%20soft%20cream%20tones%2C%20layered%20circular%20shapes%20with%20gentle%20blending%2C%20no%20objects%20no%20people%2C%20fine%20art%20quality%2C%20harmonious%20color%20transitions%2C%20contemporary%20minimalist%20composition&width=500&height=625&seq=expertise-03-bruny&orientation=portrait"
          />
          <ExpertiseCard
            title="Platform Expertise"
            description="Deep knowledge of the iOS ecosystem, its frameworks, patterns, and evolving best practices."
            imageUrl="https://readdy.ai/api/search-image?query=Abstract%20minimalist%20gradient%20in%20soft%20stone%20and%20pale%20sage%20tones%2C%20subtle%20grid%20pattern%20with%20organic%20texture%20overlay%2C%20no%20objects%20no%20people%2C%20architectural%20abstract%20quality%2C%20delicate%20and%20airy%20composition%2C%20fine%20art%20aesthetic&width=500&height=625&seq=expertise-04-bruny&orientation=portrait"
          />
        </div>
      </div>
    </section>
  );
}